import { useState } from 'react';

import { useToastStore } from '../../../stores/toastStore';
import type { CostCapRequest, CuGovernanceRequest, UpsertRoutingPolicyRequest } from '../api';
import { useUpsertRoutingPolicy } from '../hooks/usePreparationPolicies';

const COMMON_CU_FILE_TYPES = ['PDF', 'DOCX', 'PPTX', 'XLSX', 'PNG', 'JPEG', 'TIFF'];

interface CapState {
  enabled: boolean;
  usd: string;     // string-bound input so the operator can clear it; parsed on submit
  softPct: string;
  hardPct: string;
}

function emptyCap(): CapState {
  return { enabled: false, usd: '', softPct: '80', hardPct: '100' };
}

function parseCap(state: CapState): CostCapRequest | undefined {
  if (!state.enabled) return undefined;
  const usd = Number(state.usd);
  const softPct = Number(state.softPct);
  const hardPct = Number(state.hardPct);
  if (!Number.isFinite(usd) || usd <= 0) return undefined;
  return { usd, softPct, hardPct };
}

export function PreparationCuGovernancePage(): JSX.Element {
  const pushToast = useToastStore((s) => s.push);
  const { mutateAsync, isPending } = useUpsertRoutingPolicy();

  const [tenantId, setTenantId] = useState('');
  const [tenantEnabled, setTenantEnabled] = useState(false);
  const [allowedDocTypeIdsCsv, setAllowedDocTypeIdsCsv] = useState('');
  const [cuFileTypes, setCuFileTypes] = useState<string[]>(['PDF']);
  const [dailyCap, setDailyCap] = useState<CapState>({ ...emptyCap(), enabled: true, usd: '5' });
  const [monthlyCap, setMonthlyCap] = useState<CapState>({ ...emptyCap(), enabled: true, usd: '120' });

  function toggleFileType(ft: string): void {
    setCuFileTypes((prev) => prev.includes(ft) ? prev.filter((x) => x !== ft) : [...prev, ft]);
  }

  async function handleSave(): Promise<void> {
    const trimmed = tenantId.trim();
    if (!trimmed) {
      pushToast({ tone: 'error', title: 'Introduce un tenantId antes de guardar.' });
      return;
    }

    const allowedDocTypeIds = allowedDocTypeIdsCsv
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const cuGovernance: CuGovernanceRequest = {
      tenantEnabled,
      allowedDocumentTypeIds: allowedDocTypeIds,
      cuFileTypes,
    };
    const daily = parseCap(dailyCap);
    if (daily) cuGovernance.dailyCap = daily;
    const monthly = parseCap(monthlyCap);
    if (monthly) cuGovernance.monthlyCap = monthly;

    const request: UpsertRoutingPolicyRequest = {
      preferredChain: [],
      forbiddenRoutes: [],
      cuGovernance,
    };

    try {
      await mutateAsync({ tenantId: trimmed, request });
      pushToast({ tone: 'success', title: 'Política CU guardada.' });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: err instanceof Error ? err.message : 'No se pudo guardar la política.',
      });
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Gobierno de coste — Content Understanding (R3/R5)</h1>
        <p className="mt-1 text-sm text-slate-500">
          R3 y R5 son las rutas facturables del pipeline. Sin caps, un mal config dispara la factura.
          Defaults sugeridos: $5/día y $120/mes; tenantEnabled <strong>OFF</strong> por defecto.
        </p>
      </header>

      <div className="card border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <strong>⚠ Activar CU sin caps es un riesgo de coste real.</strong> El router solo despacha R3/R5
        cuando los 5 controles pasan (tenant ON · doctype permitido · MIME permitido · daily cap · monthly cap).
        Cualquier fallo cae automáticamente a R2/R4 sin error visible al usuario.
      </div>

      <section className="card space-y-4 p-5">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">Tenant ID</span>
          <input
            type="text"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="tenant_abc123"
            className="w-full max-w-md rounded border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>

        <label className="flex items-center gap-3 pt-2">
          <input
            type="checkbox"
            checked={tenantEnabled}
            onChange={(e) => setTenantEnabled(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300"
          />
          <span className="text-sm font-medium text-slate-800">
            Habilitar Content Understanding para este tenant
          </span>
        </label>
      </section>

      <section className="card space-y-3 p-5">
        <h2 className="text-sm font-medium text-slate-700">Document types permitidos</h2>
        <p className="text-xs text-slate-500">
          IDs separados por coma. Solo estos tipos podrán activar R3. Si está vacío, R3 nunca se dispara
          aunque el tenant esté habilitado.
        </p>
        <input
          type="text"
          value={allowedDocTypeIdsCsv}
          onChange={(e) => setAllowedDocTypeIdsCsv(e.target.value)}
          placeholder="dt_invoice_es, dt_contract_es"
          className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs focus:border-primary focus:ring-1 focus:ring-primary"
        />
      </section>

      <section className="card space-y-3 p-5">
        <h2 className="text-sm font-medium text-slate-700">Tipos de fichero permitidos para CU</h2>
        <p className="text-xs text-slate-500">
          Se mapean a <code className="font-mono">cu_file_types</code> del sidecar. Un MIME fuera de esta
          lista nunca llega a CU.
        </p>
        <div className="flex flex-wrap gap-2">
          {COMMON_CU_FILE_TYPES.map((ft) => {
            const on = cuFileTypes.includes(ft);
            return (
              <button
                type="button"
                key={ft}
                onClick={() => toggleFileType(ft)}
                className={`rounded px-3 py-1 text-xs font-medium ring-1 ${
                  on
                    ? 'bg-primary text-white ring-primary'
                    : 'bg-slate-50 text-slate-700 ring-slate-200 hover:bg-slate-100'
                }`}
              >
                {ft}
              </button>
            );
          })}
        </div>
      </section>

      <CapEditor title="Cap diario" state={dailyCap} onChange={setDailyCap} />
      <CapEditor title="Cap mensual" state={monthlyCap} onChange={setMonthlyCap} />

      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={isPending}
          className="btn-primary disabled:opacity-50"
        >
          {isPending ? 'Guardando…' : 'Guardar política CU'}
        </button>
      </div>
    </div>
  );
}

interface CapEditorProps {
  title: string;
  state: CapState;
  onChange: (next: CapState) => void;
}

function CapEditor({ title, state, onChange }: CapEditorProps): JSX.Element {
  return (
    <section className="card space-y-3 p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-slate-700">{title}</h2>
        <label className="inline-flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={state.enabled}
            onChange={(e) => onChange({ ...state, enabled: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300"
          />
          activo
        </label>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">USD límite</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={state.usd}
            disabled={!state.enabled}
            onChange={(e) => onChange({ ...state, usd: e.target.value })}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400 focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">Soft %</span>
          <input
            type="number"
            min={0}
            max={100}
            value={state.softPct}
            disabled={!state.enabled}
            onChange={(e) => onChange({ ...state, softPct: e.target.value })}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400 focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-600">Hard %</span>
          <input
            type="number"
            min={0}
            max={100}
            value={state.hardPct}
            disabled={!state.enabled}
            onChange={(e) => onChange({ ...state, hardPct: e.target.value })}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400 focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </label>
      </div>
    </section>
  );
}
